<template>
  <div class="position-fixed w-100">
    <div id="top"
         class="w-100">
      <TitleBar />
    </div>
    <!--div 
      v-if="!!infoLabel?.active" 
      id="info"
    >
      <span class="info-text">{{ infoLabel.text }}</span>
      <a
        v-if="infoLabel.link && infoLabel.cta_text"
        id="info-button"
        :href="`${domain}${infoLabel.link}?agencyId=${agencyId}&expiCode=${expiCode}`"
        target="_blank"
      >
        {{ infoLabel.cta_text }}
      </a>
    </div-->

    <div id="main"
         class="position-fixed w-100 d-flex"
         :class="{ infoLabel: !!infoLabel?.text }">
      <FilterPanel />
      <CruiseList />
      <DetailsPanel />
    </div>
  </div>
</template>

<script lang="ts">
import { useCatalogStore, useFilterStore } from '@/store'
import { useAppStore } from '@/store/app'
import { bewotecMicroServiceApi } from '@/store/app/helper'
import { IDomainList, Stage } from '@/store/app/interfaces'
import { useUserStore } from '@/store/user'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { defineComponent } from 'vue'
import { generateRequestId, restoreURL } from '../general/helper'
import TitleBar from './TitleBar.vue'
import CruiseList from './cruise-list/CruiseList.vue'
import DetailsPanel from './details/DetailsPanel.vue'
import FilterPanel from './filter/FilterPanel.vue'

export default defineComponent({
  components: {
    TitleBar,
    FilterPanel,
    CruiseList,
    DetailsPanel,
  },

  computed: {
    ...mapState(useAppStore, {
      infoLabel: store => store.infoLabel,
    }),
    ...mapWritableState(useAppStore, {
      stage: 'stage'
    }),
    ...mapState(useUserStore, {
      agencyId: store => store.agencyId,
      expiCode: store => store.expiCode
    }),
    ...mapWritableState(useCatalogStore, {
      requestId: 'requestId'
    }),

    domain: () => {
      const domainlist: IDomainList = {
        prod: 'https://aida.de',
        test: 'https://stage.aida.de',
        dev: 'http://localhost:4000',
      }
      return domainlist[process.env.STAGE as keyof IDomainList]
    },
    infoUrl() {
      const domain = this.domain
      const path = '/b2b-finden-exp'

      return domain + path + '?agencyId=' + this.agencyId + '&expiCode=' + this.expiCode
    },
  },

  async created() {
    await Promise.all([
      this.stashFilterPreset(),
      this.setBosysReference(),
      this.loginProcedure()
    ])

    if (!!this.agencyId) {
      await restoreURL()

      this.stage = process.env.STAGE as Stage

      bewotecMicroServiceApi()

      await Promise.all([
        this.loadBosysReference(),
        this.fetchFilters(),
        this.presetFilterFields(),
        this.fetchHolidays()
      ])

      this.requestId = generateRequestId('FETCAT')

      await this.fetchCatalog()
      await this.preselectFilterEvent()
    }
  },

  methods: {
    ...mapActions(useUserStore, {
      loginProcedure: 'loginProcedure'
    }),
    ...mapActions(useFilterStore, {
      stashFilterPreset: 'stashFilterPreset',
      fetchHolidays: 'fetchHolidays',
      fetchFilters: 'fetchFilters',
      presetFilterFields: 'presetFilterFields',
      preselectFilterEvent: 'preselectFilterEvent'
    }),
    ...mapActions(useCatalogStore, {
      fetchCatalog: 'fetchCatalog'
    }),
    ...mapActions(useAppStore, {
      storeBosysReference: 'storeBosysReference',
      loadBosysReference: 'loadBosysReference'
    }),

    redirectToExpinetHome: () => {
      const homeURL: IDomainList = {
        prod: 'https://www.expinet.de',
        test: 'https://stage-www.expinet.de',
        dev: 'https://stage-www.expinet.de',
      }
      window.location.href = homeURL[process.env.STAGE as keyof IDomainList]!
    },
    async setBosysReference() {
      let ref: string | null = null
      const url = window.location.href
      const index1 = url.indexOf('reference')

      if (index1 > -1) {
        const index2 = url.indexOf('=', index1)
        let index3 = url.indexOf('&', index2)

        if (index3 === -1) {
          index3 = url.length
        }
        ref = url.substring(index2 + 1, index3)
      }

      await this.storeBosysReference(ref)
    },
  },
})
</script>

<style lang="scss">
@import '../scss/expinet.scss';
@import '../scss/styles.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

@font-face {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  src: local('Open Sans'), local('OpenSans'),
    url('https://carol.cruise-api.aida.de/i18n-bea/de/defaultfont-regular-woff2-wfs6ew-original.woff2') format('woff2'),
    url('https://carol.cruise-api.aida.de/i18n-bea/de/defaultfont-regular-woff-xx167z-original.woff') format('woff');
}

@font-face {
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 700;
  src: local('Open Sans Bold'), local('OpenSans-Bold'),
    url('https://carol.cruise-api.aida.de/i18n-bea/de/defaultfont-w700-woff2-3a75bm-original.woff2') format('woff2'),
    url('https://carol.cruise-api.aida.de/i18n-bea/de/defaultfont-w700-woff-bz9dkb-original.woff') format('woff');
}

@font-face {
  font-family: 'Source Serif';
  font-style: normal;
  font-weight: 400;
  src: local('Source Serif'), local('SourceSerif4-Regular')
}

html,
body {
  height: 100%;
  width: 100%;
}

* {
  box-sizing: border-box;
}

body {
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: initial;
  text-rendering: optimizeLegibility;
  -webkit-text-size-adjust: 100%;
  -moz-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  text-size-adjust: 100%;
  font-family: 'Open Sans', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;
  background-color: $color-expinet-background;
  padding: 0;
  overflow: hidden;
  color: $color-aida-black;
}

button::-moz-focus-inner,
input::-moz-focus-inner {
  border: 0;
}

input {
  border: none;
}

.source-serif {
  font-family: 'Source Serif';
}

// TODO: outsource it

.fs-10 {
  font-size: 10px;
}

.fs-12 {
  font-size: 12px;
}

.fs-14 {
  font-size: 14px;
}

.fs-16 {
  font-size: 16px;
}
</style>

<style lang="scss" scoped>
@import '../scss/expinet.scss';
@import '@gecko-akh/shared-styles/src/styles/AOS.vars.scss';

$header-height: 44px;

#top {
  left: 0;
  top: 0;
  height: 43px;
  z-index: 1;
}

// #info {
//   position: relative;
//   // TODO: change background color depending on message recieved, when label is reactivated
//   background: $color-expinet-blue-middle;
//   color: $color-aida-white;
//   padding: 10px 0;
//   font-size: 14px;
//   display: flex;
//   justify-content: center;
// }

// .info-text {
//   align-self: center;
// }

// #info-button {
//   background: $color-aida-white;
//   color: $color-expinet-blue;
//   text-decoration: none;
//   border-radius: 4px;
//   padding: 4px 15px;
//   margin-left: 10px;

//   &:hover {
//     background: $color-expinet-blue-h;
//     color: $color-aida-white;
//   }
// }

#main {
  top: $header-height;
  height: calc(100% - $header-height - 5px);

  // &.infoLabel {
  //   top: 84px; /* <- activate für info-label */
  //   height: calc(100% - 90px); /* <- activate für info-label */
  // }
}
</style>
